'use client'

import { MapPin, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RawStoreData } from '@/lib/stores/store-types'
import { getTodaysHours } from '@/lib/store-hours'
import Link from 'next/link'
import { Locale } from '@/lib/i18n'
import { formatPhoneNumber } from '@/lib/i18n/format-phone-number'
import { StoreMarkerIcon } from './StoreMarkerIcon'
import { getFullStreetAddress, isStoreVeryClosed } from '@/lib/utils'

export default function StoreListItem({
	locale,
	store,
	dictionary,
	highlightStore,
	currentTime,
}: {
	locale: Locale
	store: RawStoreData
	dictionary: Record<string, string>
	highlightStore: (store: RawStoreData) => void
	currentTime: Date
}) {
	const { isOpen, day, start, end, hoursNotAvailable } = getTodaysHours(store, currentTime)
	const isVeryClosed = isStoreVeryClosed(store)
	return (
		<Link
			href={`/${locale}/store/${store.id}`}
			onClick={(e) => {
				if (window.innerWidth <= 768) {
					return // Let the Link's href handle navigation
				}
				e.preventDefault()
				highlightStore(store)
			}}
			className="flex items-start space-x-3 hover:bg-gray-100 p-2 rounded-md"
			key={store.id}
			role="article"
			aria-label={`${store.name} - ${getFullStreetAddress(store.address)}, ${store.address.city}, ${store.address.state} ${store.address.zip}`}
		>
			<StoreMarkerIcon storeType={store.store_type} size={24} aria-hidden="true" />
			<div>
				<h2 className="font-bold text-sm">{store.name}</h2>
				<p className="text-sm">{getFullStreetAddress(store.address)}</p>
				<p className="text-sm">
					{store.address.city}, {store.address.state} {store.address.zip}
				</p>
				{store.phone && (
					<Button
						size="sm"
						variant="link"
						className="text-sm text-blue-600 p-0"
						onClick={(e) => {
							e.preventDefault()
							window.open(`tel:${store.phone}`)
						}}
						aria-label={`${dictionary['call-store']} ${formatPhoneNumber(store.address.country, store.phone)}`}
					>
						{formatPhoneNumber(store.address.country, store.phone)}
					</Button>
				)}
				{!hoursNotAvailable && (
					<div className="flex items-center mt-1">
						{!isVeryClosed && (
							<>
								<div
									className={`w-3 h-3 rounded-full mr-2 ${isOpen ? 'bg-green-500' : 'bg-red-500'}`}
									role="img"
									aria-label={isOpen ? dictionary['store-open'] : dictionary['store-closed']}
								></div>
								{isOpen && (
									<span className="text-sm">
										{day} {start} - {end}
									</span>
								)}
								{!isOpen && <span className="text-sm">{dictionary['closed-right-now']}</span>}
							</>
						)}
						{store.openInfo === 'CLOSED_PERMANENTLY' && (
							<span className="text-red-600 text-sm">{dictionary['permanently-closed']}</span>
						)}
						{store.openInfo === 'CLOSED_TEMPORARILY' && (
							<span className="text-orange-600 text-sm">{dictionary['temporarily-closed']}</span>
						)}
					</div>
				)}

				<div className="flex flex-row items-center space-x-2">
					<div className="mt-2 flex flex-row gap-2">
						<Link
							href={`https://www.google.com/maps/dir/?api=1&destination=${store.coordinates.latitude},${store.coordinates.longitude}`}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => {
								// the container is a link so we need to stop the event from bubbling up to the parent link
								e.stopPropagation()
							}}
						>
							<Button
								size="sm"
								className="bg-black hover:bg-gray-800 text-white text-xs px-3 rounded"
								title={dictionary['get-directions']}
								aria-label={dictionary['get-directions']}
							>
								<MapPin size={16} aria-hidden="true" />
							</Button>
						</Link>
						<Link
							href={`/${locale}/store/${store.id}`}
							onClick={(e) => {
								// the container is a link so we need to stop the event from bubbling up to the parent link
								e.stopPropagation()
							}}
						>
							<Button
								size="sm"
								className="bg-black hover:bg-gray-800 text-white text-xs px-3 rounded sm:block hidden"
								title={dictionary['view-store-details']}
								aria-label={dictionary['view-store-details']}
							>
								<ShoppingBag size={16} aria-hidden="true" />
							</Button>
						</Link>
					</div>
					<div className="mt-2 text-sm">
						{store.distance} {dictionary['miles']}
					</div>
				</div>
			</div>
		</Link>
	)
}
